import { AnimatePresence, motion } from 'framer-motion';
import { ComponentProps, useState } from 'react';

import useEffectWithoutMount from '../../hooks/react/useEffectWithoutMount';
import Card from '../Card/Card';
import PaddedBox from '../PaddedBox/PaddedBox';

import styles from './DimmableCard.module.css';

type DimmableCardProps = {
    dimmed?: boolean;
    dimDelay?: number;
} & ComponentProps<typeof Card>;

const DimmableCard = ({
    children,
    dimmed = false,
    dimDelay = 0,
    isLast = true,
    isFirst = true,
    variant = 'white',
    horizontalPadding,
    verticalPadding,
    ...cardProps
}: DimmableCardProps) => {
    const [shouldDim, setShouldDim] = useState(dimmed);

    useEffectWithoutMount(() => {
        let timeout: ReturnType<typeof setInterval>;

        if (dimmed) {
            if (dimDelay === 0) {
                setShouldDim(true);
            } else {
                timeout = setTimeout(() => setShouldDim(true), dimDelay * 1000);
            }
        } else {
            setShouldDim(false);
        }

        return () => clearTimeout(timeout);
    }, [dimmed, dimDelay]);

    return (
        <Card
            {...cardProps}
            isLast={isLast}
            isFirst={isFirst}
            variant={variant}
            horizontalPadding={horizontalPadding}
            verticalPadding={verticalPadding}
        >
            {children}

            <AnimatePresence>
                {shouldDim && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                    >
                        <PaddedBox
                            className={styles.Overlay}
                            variant={variant}
                            horizontalPadding={horizontalPadding}
                            verticalPadding={verticalPadding}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </Card>
    );
};

export default DimmableCard;
